// apps/scheduler-worker/src/pages/ProfilePage.tsx
import { useState, useEffect } from 'react';
import { User, Wrench, Wifi, WifiOff, Database, AlertCircle } from 'lucide-react';
import { useProfile } from '../hooks/useProfile';
import { useSyncService } from '../hooks/useSyncService';
import { usePendingSync } from '../hooks/usePendingSync';
import { getCachedTaskList, clearCachedTasks } from '../lib/db';
import { Skeleton } from '../components/LoadingSkeleton';
import { Button } from 'scheduler-ui';

function ProfileSkeleton() {
  return (
    <div className="p-4 space-y-4">
      {/* Profile info skeleton */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-4">
          <Skeleton className="h-16 w-16 rounded-full" />
          <div className="space-y-2 flex-1">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
        </div>
      </div>
      {/* Skills skeleton */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
        <Skeleton className="h-5 w-20" />
        <div className="flex gap-2">
          <Skeleton className="h-6 w-20 rounded-full" />
          <Skeleton className="h-6 w-24 rounded-full" />
        </div>
      </div>
      {/* Sync skeleton */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-4 w-48" />
      </div>
    </div>
  );
}

export function ProfilePage() {
  const { data: profile, isLoading, error } = useProfile();
  const { isOnline } = useSyncService();
  const { pendingCount } = usePendingSync();
  const [cachedTaskCount, setCachedTaskCount] = useState(0);
  const [isClearing, setIsClearing] = useState(false);

  useEffect(() => {
    getCachedTaskList()
      .then((tasks) => setCachedTaskCount(tasks.length))
      .catch(() => setCachedTaskCount(0));
  }, []);

  const handleClearCache = async () => {
    setIsClearing(true);
    try {
      await clearCachedTasks();
      setCachedTaskCount(0);
    } catch {
      // Silently handle - IndexedDB may not be available
    } finally {
      setIsClearing(false);
    }
  };

  if (isLoading) {
    return <ProfileSkeleton />;
  }

  if (error && isOnline) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-700 mb-2">
            <AlertCircle className="h-5 w-5" />
            <span className="font-medium">Failed to load profile</span>
          </div>
          <p className="text-sm text-red-600">
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </div>
      </div>
    );
  }

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <div className="p-4 space-y-4">
      {/* Profile Info */}
      <div data-testid="profile-info" className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
            <User className="h-8 w-8 text-blue-600" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 truncate">
              {profile?.name ?? 'Unknown User'}
            </h2>
            <p className="text-sm text-gray-500 truncate">
              {profile?.email ?? ''}
            </p>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                {profile?.role ?? 'worker'}
              </span>
              {profile?.targetCapacityPercent != null && profile.targetCapacityPercent !== 100 && (
                <span className="text-xs text-gray-500">
                  {profile.targetCapacityPercent}% capacity
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Skills */}
      {profile?.skills && profile.skills.length > 0 && (
        <div data-testid="profile-skills" className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Wrench className="h-4 w-4 text-gray-500" />
            <h3 className="text-sm font-medium text-gray-700">Skills</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {profile.skills.map((skill) => (
              <span
                key={skill.id}
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700"
              >
                {skill.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Connection & Sync */}
      <div data-testid="profile-sync" className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          {isOnline ? (
            <Wifi className="h-4 w-4 text-green-500" />
          ) : (
            <WifiOff className="h-4 w-4 text-red-500" />
          )}
          <h3 className="text-sm font-medium text-gray-700">Connection & Sync</h3>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Status</span>
            <span className={`text-sm font-medium ${isOnline ? 'text-green-600' : 'text-red-600'}`}>
              {isOnline ? 'Online' : 'Offline'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Pending sync</span>
            <span className={`text-sm font-medium ${pendingCount > 0 ? 'text-amber-600' : 'text-gray-900'}`}>
              {pendingCount} {pendingCount === 1 ? 'entry' : 'entries'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Timezone</span>
            <span className="text-sm text-gray-900">{timezone}</span>
          </div>
        </div>
      </div>

      {/* Offline Storage */}
      <div data-testid="profile-storage" className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Database className="h-4 w-4 text-gray-500" />
          <h3 className="text-sm font-medium text-gray-700">Offline Storage</h3>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Cached tasks</span>
            <span className="text-sm text-gray-900">{cachedTaskCount}</span>
          </div>
          <Button
            data-testid="clear-cache-button"
            variant="secondary"
            size="sm"
            onClick={handleClearCache}
            disabled={isClearing || cachedTaskCount === 0}
          >
            {isClearing ? 'Clearing...' : 'Clear Cache'}
          </Button>
        </div>
      </div>
    </div>
  );
}
